import React, { useState } from "react";
import { DownloadVideoModal } from "../../../../components/MenuOptions";
import {
  buildDownloadFileName,
  DownloadType,
  formatName,
  IFile,
  processSingleVideoDownload,
} from "./helpers";
import { useDownloadGate } from "./useDownloadGate";
import { IVideo } from "../hooks/useFetchVideos";
import { getRenamedDownload } from "../../../../utils/downloadHelpers";
import {
  VideoPanel,
  PanelEmpty,
  PanelError,
  PanelSkeleton,
  PanelAction,
  ImageTile,
  IconImageGlyph,
  IconDownloadGlyph,
} from "./panels";
import type { PanelStatus } from "./panels";

interface Props {
  /** The video's project, so "All" uses the same gate as the header Download. */
  project?: any;
  video: IVideo;
  thumbnails: Array<IFile>;
  videoName?: string;
  bucket?: string;
  status: PanelStatus;
  onRetry: () => void;
}

/**
 * Extensions this panel can actually be handed. Matching a fixed list — rather
 * than "whatever the last word is" — is what keeps an arbitrary uploaded name
 * intact: "Kitchen front view" has to stay whole, not read "Kitchen front · VIEW".
 */
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif", "tif", "tiff", "avif", "heic", "bmp"];

const isImageExtension = (ext: string) => IMAGE_EXTENSIONS.includes(ext.toLowerCase());

/**
 * The generated variants in the design's wording, keyed on what survives the
 * prefix. A variant we don't know keeps its own tail rather than being dropped.
 */
const GENERATED_LABELS: Record<string, string> = {
  "Play Icon Thumbnail": "Play-icon overlay",
};

/**
 * Splits a thumbnail name into the part that identifies it and its format, so
 * the tile can truncate the first and always show the second.
 *
 * Every generated name `fetchThumbnails` builds opens with the same
 * "Auto Generated .png " and differs only at the very end, so the tile's
 * trailing ellipsis was eating the one word that told them apart —
 * "Auto Generated .png Play Ico…". Real uploads carry arbitrary names, so each
 * branch below is gated on a recognised image extension and otherwise falls
 * through to the name exactly as given.
 *
 *   "Auto Generated .png Play Icon Thumbnail" → { label: "Play-icon overlay", ext: "PNG" }
 *   "Thumbnail 1 jpg"                        → { label: "Thumbnail 1",       ext: "JPG" }
 *   "kitchen-hero.png"                       → { label: "kitchen-hero",      ext: "PNG" }
 *   "Kitchen front view"                     → { label: "Kitchen front view" }
 */
export function splitThumbnailName(name: string): { label: string; ext?: string } {
  const raw = (name || "").trim();

  const generated = /^Auto Generated\s+\.?([A-Za-z0-9]+)\s+(.+)$/i.exec(raw);
  if (generated && isImageExtension(generated[1])) {
    const tail = generated[2].trim();
    return { label: GENERATED_LABELS[tail] || tail, ext: generated[1].toUpperCase() };
  }

  // Greedy, so the LAST separator wins and a name that carries dots of its own
  // ("Screenshot 10.42.11.png") loses only the real extension.
  const trailing = /^(.+)[\s.]+([A-Za-z0-9]+)$/.exec(raw);
  if (trailing && isImageExtension(trailing[2])) {
    const label = trailing[1].trim();
    if (label) return { label, ext: trailing[2].toUpperCase() };
  }

  return { label: raw };
}

export function ThumbnailsList({ video, project, thumbnails, videoName, bucket, status, onRetry }: Props) {
  const [downloadSource, setDownloadSource] = useState<string>();
  const [downloadFileName, setDownloadFileName] = useState<string>();
  const [zipping, setZipping] = useState(false);
  // Same gate as the Download button in the header — Thumbnails is in
  // ALWAYS_ON_FILE_TYPES, so only the approval rule can withhold it.
  const { canDownload } = useDownloadGate(video, project);

  const downloadAll = async () => {
    setZipping(true);
    try {
      const result = await processSingleVideoDownload(
        video,
        DownloadType.THUMB,
        `${formatName(video)}-thumbnails.zip`,
      );
      if (result.error || !result.source) return;
      setDownloadFileName(undefined);
      setDownloadSource(result.source);
    } finally {
      setZipping(false);
    }
  };

  // Per-tile download is unchanged — same presign, same filename, same modal.
  const handleDownload = async (file: IFile) => {
    const { link, fileName } = await getRenamedDownload({
      bucket,
      key: file.key,
      fileName: buildDownloadFileName(file.key || file.url, videoName || formatName(video), video),
      fallbackUrl: file.url,
    });
    setDownloadFileName(fileName);
    setDownloadSource(link);
  };

  const body = () => {
    if (status === "loading") return <PanelSkeleton variant="tiles" />;
    if (status === "error") {
      return (
        <PanelError
          icon={<IconImageGlyph size={24} strokeWidth={1.6} />}
          title="Couldn't load thumbnails"
          onRetry={onRetry}
          grow
        />
      );
    }
    if (thumbnails.length === 0) {
      return (
        <PanelEmpty
          icon={<IconImageGlyph size={24} strokeWidth={1.6} />}
          title="No thumbnails"
          body="None were generated or uploaded for this file."
          grow
        />
      );
    }
    return (
      // auto-fill rather than a fixed column count: the same grid gives a wide
      // stack today and a 2-up when this panel moves into the narrow asset row.
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}
      >
        {thumbnails.map((thumbnail) => {
          const { label, ext } = splitThumbnailName(thumbnail.name);
          return (
            <ImageTile
              key={thumbnail.key || thumbnail.url}
              src={thumbnail.url}
              caption={label}
              captionExt={ext}
              // Exact type, not a substring of the URL. The URL carries the video's
              // own filename, so "mul*tif*unction", "Case*tif*y" and "*Tif*fany" — all
              // real keys — rendered every thumbnail as the glyph plate.
              glyphOnly={thumbnail.type === "tif"}
              onDownload={() => handleDownload(thumbnail)}
            />
          );
        })}
      </div>
    );
  };

  return (
    <VideoPanel
      id="panel-thumbnails"
      title="Thumbnails"
      count={status === "ready" ? thumbnails.length : undefined}
      action={
        status === "ready" && thumbnails.length > 0 ? (
          <PanelAction
            label={zipping ? "Zipping…" : "All"}
            icon={<IconDownloadGlyph size={13} strokeWidth={2} />}
            onClick={downloadAll}
            disabled={!canDownload || zipping}
            title={canDownload ? "Download all thumbnails" : "Not available for download"}
          />
        ) : undefined
      }
      fill
    >
      {body()}
      <DownloadVideoModal
        open={!!downloadSource}
        onClose={() => {
          setDownloadSource(undefined);
          setDownloadFileName(undefined);
        }}
        downloadSource={downloadSource || ""}
        fileName={downloadFileName}
      />
    </VideoPanel>
  );
}
